import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trackPageLoad } from '@fff-web/fff-analytics';
import { Button } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import { productsSelfDataQuery } from '../../queries/productsSelfDataQuery';
import { benefitNavigationLabels } from '../../utils/benefitNavigationLabels';
import breakpoints from '../../utils/breakpoints';
import getBenefits from '../../utils/getBenefits';
import { pages } from '../../utils/pages';
import { redirectToDashboard } from '../../utils/redirectMethods';
import CANCEL_TYPES from '../../utils/segment/cancelTypes';
import {
  eventCancelFlowAccessed,
  eventCancelFlowSkipped,
} from '../../utils/segment/eventsHandlers';
import CANCEL_FLOW_EXPERIENCE from '../../utils/segment/experiences';
import BreakLine from '../commons/break-line';
import useCurrentWidth from '../commons/use-current-width/useCurrentWidth';
import { Subscription, SubscriptionType } from '../container/Container';
import { DynamicTextContext } from '../context/DynamicTextContext';
import { SurveyPageContext } from '../context/SurveyPageContext';
import ActiveSubscriptionAlert from './activeSubscriptionAlert/ActiveSubscriptionAlert';
import Benefits from './benefits';
import Legal from './legal';
import SwapMyBoxModal from './modals/swap-my-box-modal';
import ShoppingCredit from './shoppingCredit';
import Title from './title';
import useBoxState from './useBoxState';
import useShoppingCreditState from './useShoppingCreditState';
import * as styles from './Welcome.module.scss';

const SHOW_SWAP_FOR = [
  'FULFILLABLE',
  'REFUND',
  'CSR',
  'CREDIT',
  'EXPIRED',
  'SUBMITTED',
  'INCOMPLETE',
  'UNKNOWN',
  'SKIPPED',
];
const LABEL_TEXT =
  'I understand that once I cancel my membership, any Shopping Credits I have will permanently expire at the end of my membership period.';

const Welcome = () => {
  const currentWidth = useCurrentWidth();
  const isDesktop = currentWidth > breakpoints.medium;
  const [checkboxShoppingCredit, setCheckboxShoppingCredit] = useState(false);
  const [checkBoxErrorShoppingCredit, setCheckboxErrorShoppingCredit] = useState(false);
  const [isActiveSubscriptionChecked, setIsActiveSubscriptionChecked] = useState(false);
  const { dynamicText } = useContext(DynamicTextContext);
  const [showModal, setShowModal] = useState(false);
  const [hasSeenCustomizeProductsModal, setHasSeenCustomizeProductsModal] = useState(false);
  const { increasePage } = useContext(SurveyPageContext);
  const { userInfo } = useUserContext();
  const subscriptionType: SubscriptionType =
    userInfo?.user?.subscriptionType === Subscription.Annual
      ? Subscription.Annual
      : Subscription.Seasonal;
  const benefits = getBenefits({ isDesktop });
  const cancelUserType = userInfo?.user?.subscriptionType?.toLowerCase() as
    | 'trial'
    | 'annual'
    | 'seasonal';
  const { key, query } = productsSelfDataQuery;
  const { data: productsSelfData } = useQuery({
    queryFn: query,
    queryKey: [key],
    retry: 2,
  });
  const { boxState } = useBoxState();
  const shoppingCredit = useShoppingCreditState();
  const hasNinetyDaySubscriptions =
    !!productsSelfData?.activeProductSubscriptions?.NINETY_DAYS?.subscriptions.length;

  const handleClickKeepButton = () => {
    eventCancelFlowSkipped({
      cta_name: benefitNavigationLabels.BACK,
      experience: CANCEL_FLOW_EXPERIENCE,
      cancel_type: CANCEL_TYPES[cancelUserType] || '',
    }).finally(() => redirectToDashboard());
  };

  const cancelAction = () => {
    if (!hasSeenCustomizeProductsModal) {
      setHasSeenCustomizeProductsModal(true);
      return;
    }

    if (shoppingCredit && !checkboxShoppingCredit) {
      setCheckboxErrorShoppingCredit(true);
      return;
    }

    increasePage();
  };

  useEffect(() => {
    eventCancelFlowAccessed({
      cta_name: benefitNavigationLabels.NEXT,
      cancel_type: `${
        subscriptionType.charAt(0) + subscriptionType.slice(1).toLowerCase()
      } Subscription`,
    });
  }, []);

  const handleClickCheckBoxShoppingCredit = (state: boolean) => {
    setCheckboxErrorShoppingCredit(false);
    setCheckboxShoppingCredit(state);
  };

  const handleActiveSubscriptionChecked = (isChecked: boolean) => {
    setIsActiveSubscriptionChecked(isChecked);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    document.title = pages.CANCEL_SUBSCRIPTION.title;
    dynamicText?.wooUserId && trackPageLoad();
  }, [dynamicText?.wooUserId]);

  useEffect(() => {
    const showSwapModal = SHOW_SWAP_FOR.includes(boxState);
    if (showSwapModal) setShowModal(true);
  }, [boxState]);

  return (
    <div className={styles.welcomeContainer} data-testid="welcome-container">
      {showModal && <SwapMyBoxModal closeAction={closeModal} />}
      <Title isDesktop={isDesktop} />
      <p className={styles.subtitle}>Don’t lose access to these exclusive membership perks:</p>
      <Benefits benefits={benefits} isDesktop={isDesktop} />
      <BreakLine />
      <div className={styles.shoppingCreditActiveSubscriptionContainer}>
        {!!shoppingCredit && (
          <ShoppingCredit
            shoppingCredit={shoppingCredit}
            label={LABEL_TEXT}
            error={checkBoxErrorShoppingCredit}
            onClick={handleClickCheckBoxShoppingCredit}
          />
        )}
        {hasNinetyDaySubscriptions && (
          <ActiveSubscriptionAlert onCheckedChange={handleActiveSubscriptionChecked} />
        )}
      </div>
      <Button onClick={handleClickKeepButton}>GIVE US ANOTHER CHANCE</Button>
      <button
        type="button"
        className={styles.endTheFun}
        onClick={cancelAction}
        disabled={hasNinetyDaySubscriptions && !isActiveSubscriptionChecked}
      >
        Cancel and end the fun <i className="fal fa-frown" />
      </button>
      <Legal />
    </div>
  );
};

export default Welcome;
